import { apronNeighbours, serviceReachOf } from './airfield';
import { BUILDINGS, type Building, BuildingType, backDirection, footprint } from './buildings';
import type { Ground } from './ground';

/*
 * Contact stands (jet bridges): where they go against a terminal, and the service road that
 * comes with one. Two go along the terminal's airside face and one at each end, never on its
 * landside (where the kerb is). Placing one lays a short service road (two tiles wide, like the
 * player's) from its side to the nearest road the ground vehicles can reach, so the player only
 * links its taxiway. The road keeps clear of the aircraft's way in (in front of the stand), of
 * the terminal's other contact stand places and of taxiways; when there is no such way within
 * SPUR_MAX_STEPS the stand is placed without one, as before.
 */

export interface ContactSlot {
  x: number;
  y: number;
  rot: number;
}

/** Faces of a footprint, outwards. */
const FACES: readonly { dx: number; dy: number }[] = [
  { dx: 0, dy: 1 },
  { dx: -1, dy: 0 },
  { dx: 0, dy: -1 },
  { dx: 1, dy: 0 },
];

/** The contact stand places round a terminal. */
export function contactSlots(t: Pick<Building, 'x' | 'y' | 'rot'>): ContactSlot[] {
  const { w, h } = footprint(BUILDINGS[BuildingType.Terminal], t.rot);
  const stand = BUILDINGS[BuildingType.ContactStand];
  const [lx, ly] = backDirection(t.rot);
  const out: ContactSlot[] = [];
  for (const { dx, dy } of FACES) {
    if (dx === lx && dy === ly) continue;
    // The stand's back faces the terminal.
    let rot = 0;
    while (rot < 4) {
      const [bx, by] = backDirection(rot);
      if (bx === -dx && by === -dy) break;
      rot++;
    }
    const size = footprint(stand, rot);
    const along = dy !== 0 ? w : h;
    const n = Math.floor(along / stand.width);
    const start = Math.floor((along - n * stand.width) / 2);
    for (let k = 0; k < n; k++) {
      const a = start + k * stand.width;
      if (dy > 0) out.push({ x: t.x + a, y: t.y + h, rot });
      else if (dy < 0) out.push({ x: t.x + a, y: t.y - size.h, rot });
      else if (dx < 0) out.push({ x: t.x - size.w, y: t.y + a, rot });
      else out.push({ x: t.x + w, y: t.y + a, rot });
    }
  }
  return out;
}

/** Furthest the service road may wind (tiles) before the stand goes without one. */
export const SPUR_MAX_STEPS = 40;
/** How far in front of the stand its way in is kept clear (tiles). */
const FRONT_CLEAR = 14;

/**
 * The service road a contact stand at (x, y, rot) set against `terminal` comes with: the tiles to
 * lay (empty when it already touches a road the vehicles reach, or stands on one apron with a
 * stand that does), or null when there is no way. The stand is not on the ground yet
 * (planning): its footprint counts as taken.
 */
export function serviceSpur(
  g: Ground,
  buildings: readonly Building[],
  terminal: Pick<Building, 'x' | 'y' | 'rot'>,
  x: number,
  y: number,
  rot: number,
): { x: number; y: number }[] | null {
  const { w, h } = footprint(BUILDINGS[BuildingType.ContactStand], rot);
  const reached = serviceReachOf(g.width, g.height, g.network, buildings);
  const inside = (tx: number, ty: number) => tx >= x && tx < x + w && ty >= y && ty < y + h;
  const isReached = (tx: number, ty: number) =>
    g.inBounds(tx, ty) && reached[g.index(tx, ty)] === 1;
  // Already beside a road the vehicles reach: nothing to lay.
  for (let tx = x; tx < x + w; tx++) if (isReached(tx, y - 1) || isReached(tx, y + h)) return [];
  for (let ty = y; ty < y + h; ty++) if (isReached(x - 1, ty) || isReached(x + w, ty)) return [];
  // Beside a stand that has one: its vehicles drive over the apron.
  const apron = apronNeighbours(
    { type: BuildingType.ContactStand, x, y, rot, level: 0 },
    buildings,
  );
  for (const n of apron) {
    const f = footprint(BUILDINGS[n.type], n.rot);
    for (let tx = n.x; tx < n.x + f.w; tx++) {
      if (isReached(tx, n.y - 1) || isReached(tx, n.y + f.h)) return [];
    }
    for (let ty = n.y; ty < n.y + f.h; ty++) {
      if (isReached(n.x - 1, ty) || isReached(n.x + f.w, ty)) return [];
    }
  }

  // Kept clear: the terminal's contact stand places, and the way in in front of this one.
  const keep = new Set<number>();
  const stand = BUILDINGS[BuildingType.ContactStand];
  for (const s of contactSlots(terminal)) {
    const f = footprint(stand, s.rot);
    for (let ty = s.y; ty < s.y + f.h; ty++) {
      for (let tx = s.x; tx < s.x + f.w; tx++) if (g.inBounds(tx, ty)) keep.add(g.index(tx, ty));
    }
  }
  const [bx, by] = backDirection(rot);
  const [fx, fy] = [-bx, -by];
  for (let d = 1; d <= FRONT_CLEAR; d++) {
    if (fy !== 0) {
      const ty = fy > 0 ? y + h - 1 + d : y - d;
      for (let tx = x - 1; tx <= x + w; tx++) if (g.inBounds(tx, ty)) keep.add(g.index(tx, ty));
    } else {
      const tx = fx > 0 ? x + w - 1 + d : x - d;
      for (let ty = y - 1; ty <= y + h; ty++) if (g.inBounds(tx, ty)) keep.add(g.index(tx, ty));
    }
  }
  const free = (tx: number, ty: number) =>
    g.inBounds(tx, ty) &&
    !inside(tx, ty) &&
    g.tileOwned(tx, ty) &&
    g.occupantAt(tx, ty) === 0 &&
    g.networkKindAt(tx, ty) === 0 &&
    !keep.has(g.index(tx, ty));
  // A 2×2 block of road with its top-left corner at (bx, by).
  const blockFree = (cx: number, cy: number) =>
    free(cx, cy) && free(cx + 1, cy) && free(cx, cy + 1) && free(cx + 1, cy + 1);
  const touches = (cx: number, cy: number, test: (tx: number, ty: number) => boolean) => {
    for (let ty = cy; ty < cy + 2; ty++) {
      for (let tx = cx; tx < cx + 2; tx++) {
        if (test(tx + 1, ty) || test(tx - 1, ty) || test(tx, ty + 1) || test(tx, ty - 1))
          return true;
      }
    }
    return false;
  };
  // Start beside the stand's sides (not its back, the terminal's, nor its front, the aircraft's).
  const lateral = (tx: number, ty: number) =>
    bx === 0 ? inside(tx - 1, ty) || inside(tx + 1, ty) : inside(tx, ty - 1) || inside(tx, ty + 1);
  const W = g.width;
  const prev = new Map<number, number>();
  const depth = new Map<number, number>();
  const queue: number[] = [];
  for (let cy = y - 2; cy <= y + h; cy++) {
    for (let cx = x - 2; cx <= x + w; cx++) {
      if (!blockFree(cx, cy)) continue;
      let beside = false;
      for (let ty = cy; ty < cy + 2 && !beside; ty++) {
        for (let tx = cx; tx < cx + 2 && !beside; tx++) beside = lateral(tx, ty);
      }
      if (!beside) continue;
      const k = cy * W + cx;
      prev.set(k, -1);
      depth.set(k, 0);
      queue.push(k);
    }
  }
  let end = -1;
  for (let head = 0; head < queue.length; head++) {
    const k = queue[head] as number;
    const cx = k % W;
    const cy = (k - cx) / W;
    if (touches(cx, cy, isReached)) {
      end = k;
      break;
    }
    const d = depth.get(k) as number;
    if (d >= SPUR_MAX_STEPS) continue;
    for (const [nx, ny] of [
      [cx + 1, cy],
      [cx - 1, cy],
      [cx, cy + 1],
      [cx, cy - 1],
    ] as const) {
      const n = ny * W + nx;
      if (prev.has(n) || !blockFree(nx, ny)) continue;
      prev.set(n, k);
      depth.set(n, d + 1);
      queue.push(n);
    }
  }
  if (end < 0) return null;
  const tiles = new Map<number, { x: number; y: number }>();
  for (let k = end; k >= 0; k = prev.get(k) ?? -1) {
    const cx = k % W;
    const cy = (k - cx) / W;
    for (const [tx, ty] of [
      [cx, cy],
      [cx + 1, cy],
      [cx, cy + 1],
      [cx + 1, cy + 1],
    ] as const) {
      tiles.set(ty * W + tx, { x: tx, y: ty });
    }
  }
  return [...tiles.keys()]
    .sort((a, b) => a - b)
    .map((t) => tiles.get(t) as { x: number; y: number });
}
